import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  BASEMAP_GOOGLE_HYBRID,
  BASEMAP_GOOGLE_ROADMAP,
} from '@rsos/constants/basemapAddonNames';
import { GOOGLE } from '@rsos/constants/mapProviders';
import { MapsNotReadyError } from '@rsos/maps/interface/reverseGeocode/googleGeocoderReady';
import { loadGoogleMaps } from '@rsos/maps/providers/google/loadGoogleMaps';
import { usePSAPCountryAlpha2 } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import type { StateWithSinatra } from '@rsos/sinatra/src/types/SinatraState';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';
import { captureExceptionWithScope, setGlobalTag } from '@rsos/utils/sentry';
import { getWebGLSupport, type WebGLSupport } from '@rsos/utils/webglSupport';
import type { GoogleMapOptions } from './types';

interface UseGoogleMapParams {
  /** Google maps API key */
  apiKey?: string;
  /** Google maps ID for applying custom styling (e.g., light or dark mode). */
  mapId?: string;
  /** Google maps options */
  options?: google.maps.MapOptions | null;
}

interface MapState {
  mapInstance: google.maps.Map | null;
  initialized: boolean;
}

/**
 * Tags every later event with what WebGL this browser offers, so a WebGL
 * failure inside the Maps API can be broken down by GPU, driver, and software
 * rendering. A browser with no WebGL at all also gets one event of its own,
 * since its map renders as raster and Google will not throw for it.
 */
const reportWebGLSupport = (webgl: WebGLSupport) => {
  setGlobalTag('webgl.supported', String(webgl.supported));
  setGlobalTag('webgl.software_only', String(webgl.softwareOnly));
  if (webgl.renderer) {
    setGlobalTag('webgl.renderer', webgl.renderer);
  }
  if (!webgl.supported) {
    captureExceptionWithScope(
      new Error('WebGL is unavailable; rendering the Google map as raster'),
      {},
      ['google-map-webgl-unavailable'],
    );
  }
};

/**
 * Handle loading google maps if window.google is undefined. Returns the Google
 * maps instance, a ref, and an initialized flag for use in other components.
 */
const useGoogleMap = ({
  apiKey = '',
  mapId = '',
  options = null,
}: UseGoogleMapParams) => {
  const [mapState, setMapState] = useState<MapState>({
    mapInstance: null,
    initialized: false,
  });

  const mapRef = useRef<HTMLDivElement>(null);

  const basemap = useSelector(
    (state: StateWithSinatra) => state?.sinatra?.ui?.basemap,
  );

  const isGoogleBasemap = useMemo(() => {
    return (
      basemap === BASEMAP_GOOGLE_HYBRID || basemap === BASEMAP_GOOGLE_ROADMAP
    );
  }, [basemap]);

  // Google resolves country-specific place names from this (Lake Ontario vs
  // Lake America). Fixed on the loader URL, so only the first caller's value
  // applies and it cannot be a dep of the effect below.
  const region = usePSAPCountryAlpha2();

  useEffect(() => {
    // Each mount owns its own resolution. Sharing a callback through `window`
    // meant a second mount clobbered the first, which then never initialized.
    let cancelled = false;

    const initMap = (maps: typeof google.maps) => {
      if (cancelled || !mapRef.current) return;

      const webgl = getWebGLSupport();
      reportWebGLSupport(webgl);

      const mapInstance = new maps.Map(mapRef.current, {
        ...options,
        ...(mapId && {
          mapId,
          // Vector keeps hybrid basemap labels visible, but needs WebGL. Without
          // it the Maps API throws from inside its own timers instead of
          // falling back, so ask for raster up front.
          renderingType: webgl.supported
            ? maps.RenderingType?.VECTOR
            : maps.RenderingType?.RASTER,
        }),
        mapTypeControl: options?.mapTypeControl || false,
        mapTypeId: options?.mapTypeId
          ? options.mapTypeId
          : isGoogleBasemap
            ? basemap
            : maps.MapTypeId?.ROADMAP,
      });

      // What Google actually rendered, which can differ from what was asked.
      mapInstance.addListener('renderingtype_changed', () => {
        setGlobalTag(
          'gmap.rendering_type',
          String(mapInstance.getRenderingType()),
        );
      });

      setMapState({ mapInstance, initialized: true });

      exportToGlobalNamespace('map', {
        mapProvider: GOOGLE,
        mapInstance,
      } satisfies GoogleMapOptions);
    };

    // 'marker' as well as 'maps', because marker creation reads
    // maps.marker.AdvancedMarkerElement synchronously and silently falls back
    // to the deprecated Marker when it is not registered yet.
    loadGoogleMaps(['maps', 'marker'], { apiKey, region })
      .then(initMap)
      .catch(error => {
        if (cancelled) return;
        if (error instanceof MapsNotReadyError) {
          // Expected when the API cannot load at all (no key, offline). Warn
          // rather than error so Sentry's console integration stays quiet.
          // eslint-disable-next-line no-console
          console.warn(error.message);
          return;
        }
        captureExceptionWithScope(error as Error);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId]);

  return { mapRef, ...mapState };
};

export default useGoogleMap;
